import React, { useState } from 'react';
import { Flame } from 'lucide-react';
import { STAGES, useCandidates } from '../../lib/useCandidates';
import CandidateRow from './CandidateRow';
import { ACTION_STYLE } from './Verdict';

const Chip = ({ active, onClick, children, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
            active ? 'border-orange-400/60 bg-orange-500/15 text-orange-100' : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
        }`}
    >
        {children}
    </button>
);

const LEGEND = [
    ['AC', 'resmi sinyal — bot tam slotla açar'],
    ['KUCUK_AC', 'hareket başladı / patlama etiketi yüksek — bot tam slotla açar'],
    ['EMIR_KOY', 'tetik bekleniyor — bot koşullu emri koyar, hacimle kırılınca açar'],
    ['ADAY', 'kanıt eksik, sadece izle — bot açmaz'],
];
const LEGEND_CLS = { AC: ACTION_STYLE.AC.cls, KUCUK_AC: ACTION_STYLE.KUCUK_AC.cls, EMIR_KOY: ACTION_STYLE.EMIR_KOY.cls, ADAY: ACTION_STYLE.BEKLE.cls };

/** 🔥 Bugün ne yapmalı — every "something may happen" coin in ONE ranked list, same words as Telegram, with the bot's state per coin. */
export default function CandidatesPanel({ rows, onSelect, tickPrices, botStatus }) {
    const { list, counts, late } = useCandidates(rows);
    const [dir, setDir] = useState('all');
    const [stage, setStage] = useState('all');
    const [favOnly, setFavOnly] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const shown = list.filter((c) => (dir === 'all' || c.v.direction === dir) && (stage === 'all' || c.stage === stage) && (!favOnly || c.row.is_favorite));
    const limit = showAll ? 60 : 8;
    const actionable = counts.AC + counts.KUCUK_AC + counts.EMIR_KOY;
    const bySym = botStatus?.by_symbol || {};
    const block = botStatus?.block || null;
    const botOpen = Object.values(bySym).filter((b) => b.state === 'open').length;
    const botPending = Object.values(bySym).filter((b) => b.state === 'pending').length;
    return (
        <div data-testid="candidates-panel" className="border border-orange-500/30 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-700 space-y-3">
                <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
                    <Flame className="h-5 w-5 text-orange-400" />
                    <span className="text-sm sm:text-base font-bold text-slate-50">Bugün ne yapmalı</span>
                    <span className="text-xs text-slate-300" data-testid="candidates-count">
                        <b className="text-orange-200">{actionable}</b> işlem yapılabilir · {counts.ADAY} izlenen{late ? ` · ${late} coin koşmuş (aşağıdaki tabloda)` : ''}
                    </span>
                    {botStatus && (
                        <span className="text-xs text-slate-300" data-testid="candidates-bot-summary">
                            🤖 {block ? <b className="text-amber-200">{block}</b> : <>bot <b className="text-emerald-200">{botOpen} açık</b> · <b className="text-amber-200">{botPending} tetik bekliyor</b></>}
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5" data-testid="candidates-legend">
                    {LEGEND.map(([k, text]) => (
                        <div key={k} className="flex items-start gap-2 text-xs text-slate-300">
                            <span className={`shrink-0 inline-flex items-center gap-1 border rounded-md px-1.5 py-0.5 text-[11px] font-black ${LEGEND_CLS[k]}`}>{STAGES[k].emoji} {STAGES[k].verb}</span>
                            <span className="leading-snug">= {text}</span>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2 overflow-x-auto pb-0.5">
                    {[['all', 'Hepsi'], ['long', '▲ Yukarı'], ['short', '▼ Aşağı']].map(([k, l]) => (
                        <Chip key={k} active={dir === k} onClick={() => setDir(k)} testId={`candidates-filter-dir-${k}`}>{l}</Chip>
                    ))}
                    <span className="text-slate-700 self-center">|</span>
                    <Chip active={stage === 'all'} onClick={() => setStage('all')} testId="candidates-filter-stage-all">Tümü</Chip>
                    {Object.values(STAGES).map((s) => (
                        <Chip key={s.key} active={stage === s.key} onClick={() => setStage(s.key)} testId={`candidates-filter-stage-${s.key}`}>
                            {s.emoji} {s.verb} <span className="text-slate-400 font-normal">{counts[s.key]}</span>
                        </Chip>
                    ))}
                    <span className="text-slate-700 self-center">|</span>
                    <Chip active={favOnly} onClick={() => setFavOnly((v) => !v)} testId="candidates-fav-toggle">⭐ Favorilerim</Chip>
                </div>
            </div>
            <div className="p-3 space-y-2">
                {shown.length === 0 ? (
                    <div className="text-sm text-slate-400 py-4 text-center" data-testid="candidates-empty">
                        {(rows || []).length === 0 ? 'Tarama yükleniyor — ilk sonuçlar 30-60 sn içinde gelir.' : list.length === 0 ? 'Şu an aday yok — piyasa sakin. Liste 25 saniyede bir yenilenir.' : 'Bu filtrede aday yok.'}
                    </div>
                ) : (
                    shown.slice(0, limit).map((c) => <CandidateRow key={c.symbol} c={c} onSelect={onSelect} tick={tickPrices?.[c.symbol]} bot={bySym[c.symbol]} block={block} />)
                )}
                {shown.length > limit && (
                    <button type="button" data-testid="candidates-show-all" onClick={() => setShowAll(true)}
                            className="w-full rounded-lg border border-slate-600 py-2 text-sm font-semibold text-slate-100 hover:border-slate-400">
                        Tümünü göster (+{shown.length - limit} coin daha)
                    </button>
                )}
                {showAll && shown.length > 8 && (
                    <button type="button" data-testid="candidates-show-less" onClick={() => setShowAll(false)}
                            className="w-full rounded-lg border border-slate-700 py-1.5 text-xs text-slate-400 hover:border-slate-500">
                        Daha az göster
                    </button>
                )}
            </div>
        </div>
    );
}
